import { Evaluation, EvaluationCategory, EvaluationEligibilityCategory } from '@/models/gen/graphql';
import useSession from '@/state/session';

import { TrainingChecklistTableTypes } from '../../components/TrainingChecklistTable/types';
import { checkEvaluationEligibility } from './checkEvaluationEligibility';
import { getEvaluation } from './getEvaluation';
import { startEvaluation } from './startEvaluation';

export const canEvaluateCategory = (questionnaireCategoryId: string): boolean =>
  (useSession.getState().state.config?.auth?.evaluableTrainingCategoryIds || []).includes(questionnaireCategoryId);

const toEvaluation = (input: EvaluationEligibilityCategory[]) => {
  const output: Evaluation = {
    categories: input.map(
      (category): EvaluationCategory => ({
        category: {
          id: category.id,
          name: category.name,
          sortOrder: category.sortOrder,
          color: category.color,
          description: category.description,
          questionnaireAttemptId: null,
        },
        questions: [],
        canEvaluate: canEvaluateCategory(category.id),
        evaluatorGroups: [],
      })
    ),
    attempt: null,
    notes: null,
  };
  return output;
};

// checkEvaluationEligibility only returns the default category list when there's no in-progress
// attempt - once an attempt exists, eligibilityCategories comes back empty and the frozen
// getEvaluation snapshot is the sole source of truth for the checklist.
export const convertCategoriesToChecklist = (
  evaluation?: TrainingChecklistTableTypes.EvaluationDetail | null
): TrainingChecklistTableTypes.ChecklistState => {
  const categoryWithQuestionMap: Record<string, TrainingChecklistTableTypes.CategoryWithQuestions> = {};

  const rows = [...evaluation.categories]
    .sort((a, b) => a.category.sortOrder - b.category.sortOrder)
    .map((evaluationCategory): TrainingChecklistTableTypes.ChecklistItem => {
      const { id, name, sortOrder, submittedAt, submitterFullName: trainerFullName } = evaluationCategory.category;
      const { canEvaluate } = evaluationCategory;

      categoryWithQuestionMap[name] = evaluationCategory;

      const questions = evaluationCategory?.questions || [];
      const totalAnswered = questions.filter(({ question }) => question.selectedAnswerOptionId).length;

      // get the category status
      let status = TrainingChecklistTableTypes.CategoryStatus.PENDING;
      if (questions.length === 0) return { id, name, sortOrder, status, trainerFullName, canEvaluate };
      if (submittedAt) status = TrainingChecklistTableTypes.CategoryStatus.COMPLETED;
      else if (totalAnswered > 0) status = TrainingChecklistTableTypes.CategoryStatus.IN_PROGRESS;

      return { id, name, sortOrder, status, trainerFullName, canEvaluate };
    });

  return { rows, categoryWithQuestionMap };
};

// allow error to bubble up
export const loadEvaulation = async (
  userId: string
): Promise<{
  rows: TrainingChecklistTableTypes.ChecklistItem[];
  categoryWithQuestionMap: Record<string, TrainingChecklistTableTypes.CategoryWithQuestions>;
  evaluationId: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  totalAttempts: number;
}> => {
  const eligibility = await checkEvaluationEligibility(userId);
  const evaluationId = eligibility?.inProgressAttemptId || null; // determines whether or not to start a new attempt
  const evaluation = evaluationId ? await getEvaluation(evaluationId) : toEvaluation(eligibility?.categories || []);
  const converted = convertCategoriesToChecklist(evaluation);
  return {
    rows: converted.rows,
    categoryWithQuestionMap: converted.categoryWithQuestionMap,
    evaluationId,
    submittedAt: evaluation?.attempt?.submittedAt || null,
    submittedBy: evaluation?.attempt?.submittedBy || null,
    totalAttempts: eligibility?.totalAttempts || 0,
  };
};

// allow error to bubble up
export const beginEvalution = async (
  subjectUserId: string,
  evaluationId?: string | null
): Promise<{
  rows: TrainingChecklistTableTypes.ChecklistItem[];
  categoryWithQuestionMap: Record<string, TrainingChecklistTableTypes.CategoryWithQuestions>;
  evaluationId: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
}> => {
  // If no evaluationId, start a new one
  if (!evaluationId) {
    const { attemptId } = await startEvaluation(subjectUserId);
    evaluationId = attemptId;
  }
  const evaluation = await getEvaluation(evaluationId);
  if (!evaluation) throw new Error('evaluate: No evaluation found');
  const converted = convertCategoriesToChecklist(evaluation);
  return {
    rows: converted.rows,
    categoryWithQuestionMap: converted.categoryWithQuestionMap,
    evaluationId,
    submittedAt: evaluation?.attempt?.submittedAt || null,
    submittedBy: evaluation?.attempt?.submittedBy || null,
  };
};

// allow error to bubble up
export const reloadEvaluation = async (
  evaluationId: string
): Promise<{
  rows: TrainingChecklistTableTypes.ChecklistItem[];
  categoryWithQuestionMap: Record<string, TrainingChecklistTableTypes.CategoryWithQuestions>;
  submittedAt: string | null;
  submittedBy: string | null;
}> => {
  if (!evaluationId) throw new Error('reload: No evaluationId provided');
  const evaluation = await getEvaluation(evaluationId);
  if (!evaluation) throw new Error('reload: No evaluation found');
  const converted = convertCategoriesToChecklist(evaluation);
  return {
    rows: converted.rows,
    categoryWithQuestionMap: converted.categoryWithQuestionMap,
    submittedAt: evaluation?.attempt?.submittedAt || null,
    submittedBy: evaluation?.attempt?.submittedBy || null,
  };
};
